/**
 * Deliver → Notifications → Templates tab.
 * Email subject/HTML editor with variable palette, live preview, test send
 * and reset-to-default. Templates are platform-level; gated by
 * `notifications.manage` (org admin+ by default).
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { useApi } from '../../hooks/useApi';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import HtmlCodeEditor from '../../components/notifications/HtmlCodeEditor';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { SkeletonCard } from '../../components/common/SkeletonCard';
import { Send, RotateCcw, Loader2 } from 'lucide-react';

export function TemplatesTab() {
  const { get } = useApi();
  const [templates, setTemplates] = useState([]);
  const [activeKey, setActiveKey] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    try {
      const rows = await get('/notifications/templates');
      setTemplates(rows || []);
      if (!activeKey && rows?.length) setActiveKey(rows[0].key);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load templates');
    }
  };

  useEffect(() => {
    refresh().finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) return <div className="grid md:grid-cols-[260px_1fr] gap-4"><SkeletonCard /><SkeletonCard /></div>;

  const active = templates.find(t => t.key === activeKey) || templates[0];

  return (
    <div className="grid md:grid-cols-[260px_1fr] gap-4">
      <aside className="space-y-1.5" data-testid="notif-templates-list">
        {templates.map(t => (
          <button
            key={t.key}
            type="button"
            onClick={() => setActiveKey(t.key)}
            data-testid={`notif-template-${t.key}`}
            className={`w-full text-left rounded-lg border p-3 transition ${
              activeKey === t.key ? 'border-[var(--lumyn-content)] bg-[var(--lumyn-content)]/5' : 'hover:border-foreground/20'
            }`}
          >
            <div className="flex items-center justify-between gap-2 mb-1">
              <p className="text-sm font-semibold truncate">{t.name}</p>
              {t.always_on ? (
                <Badge variant="outline" className="text-[10px]">Always-on</Badge>
              ) : (
                <Badge variant={t.enabled ? 'default' : 'secondary'} className="text-[10px]">
                  {t.enabled ? 'On' : 'Off'}
                </Badge>
              )}
            </div>
            <p className="text-xs text-muted-foreground line-clamp-2">{t.description}</p>
            {t.is_overridden && (
              <p className="text-[10px] text-amber-600 mt-1">Customised</p>
            )}
          </button>
        ))}
      </aside>

      <section data-testid="notif-template-editor">
        {active ? <TemplateEditor key={active.key} template={active} onSaved={refresh} /> : null}
      </section>
    </div>
  );
}

function TemplateEditor({ template, onSaved }) {
  const { put, post } = useApi();
  const [subject, setSubject] = useState(template.subject);
  const [bodyHtml, setBodyHtml] = useState(template.body_html);
  const [enabled, setEnabled] = useState(!!template.enabled);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [testing, setTesting] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);

  useEffect(() => {
    setSubject(template.subject);
    setBodyHtml(template.body_html);
    setEnabled(!!template.enabled);
  }, [template.key, template.subject, template.body_html, template.enabled, template.updated_at]);

  const subjectRef = useRef(null);
  const bodyRef = useRef(null);
  const lastFocusedRef = useRef('body');

  const sample = useMemo(
    () => Object.fromEntries((template.variables || []).map(v => [v.key, v.sample])),
    [template]
  );

  const renderPreview = (str) =>
    (str || '').replace(/\{\{\s*(\w+)\s*\}\}/g, (m, k) =>
      sample[k] != null ? sample[k] : m
    );

  const dirty = subject !== template.subject || bodyHtml !== template.body_html
    || enabled !== !!template.enabled;

  const handleInsertVar = (varKey) => {
    const token = `{{${varKey}}}`;
    if (lastFocusedRef.current === 'subject') {
      const el = subjectRef.current;
      if (!el) return;
      const start = el.selectionStart ?? subject.length;
      const end = el.selectionEnd ?? subject.length;
      const next = subject.slice(0, start) + token + subject.slice(end);
      setSubject(next);
      requestAnimationFrame(() => {
        el.focus();
        el.setSelectionRange(start + token.length, start + token.length);
      });
    } else {
      bodyRef.current?.insertAtCursor(token);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await put(`/notifications/templates/${template.key}`, {
        subject,
        body_html: bodyHtml,
        enabled: template.always_on ? undefined : enabled,
      });
      toast.success('Template saved');
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    setResetting(true);
    try {
      await post(`/notifications/templates/${template.key}/reset`, {});
      toast.success('Template reset to defaults');
      setConfirmReset(false);
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reset failed');
    } finally {
      setResetting(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      const res = await post(`/notifications/templates/${template.key}/test`, {});
      toast.success(`Test email sent to ${res?.to || 'your inbox'}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Test send failed');
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="rounded-lg border bg-card p-4 sm:p-5 space-y-4">
      <header className="flex flex-wrap items-start justify-between gap-3 pb-3 border-b">
        <div>
          <h3 className="text-base font-semibold">{template.name}</h3>
          <p className="text-xs text-muted-foreground mt-0.5">{template.description}</p>
        </div>
        <div className="flex items-center gap-2">
          {!template.always_on && (
            <label className="flex items-center gap-2 text-sm select-none cursor-pointer">
              <Switch
                checked={enabled}
                onCheckedChange={setEnabled}
                data-testid="notif-template-enabled"
              />
              <span>{enabled ? 'Enabled' : 'Disabled'}</span>
            </label>
          )}
        </div>
      </header>

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="notif-subject">Subject</Label>
            <Input
              id="notif-subject"
              ref={subjectRef}
              value={subject}
              onChange={e => setSubject(e.target.value)}
              onFocus={() => { lastFocusedRef.current = 'subject'; }}
              data-testid="notif-subject"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="notif-body">Body (HTML)</Label>
            <HtmlCodeEditor
              ref={bodyRef}
              value={bodyHtml}
              onChange={setBodyHtml}
              onFocus={() => { lastFocusedRef.current = 'body'; }}
              testId="notif-body"
            />
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground uppercase tracking-wider">Insert variable</Label>
            <div className="flex flex-wrap gap-1.5" data-testid="notif-var-palette">
              {(template.variables || []).map(v => (
                <button
                  type="button"
                  key={v.key}
                  onClick={() => handleInsertVar(v.key)}
                  className="text-[11px] px-2 py-1 rounded-md border bg-muted/40 hover:bg-muted hover:border-foreground/30 transition font-mono"
                  title={v.label}
                  data-testid={`notif-var-${v.key}`}
                >
                  {`{{${v.key}}}`}
                </button>
              ))}
            </div>
          </div>

          <div className="flex flex-wrap gap-2 pt-2">
            <Button onClick={handleSave} disabled={!dirty || saving} data-testid="notif-save">
              {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : null}
              Save changes
            </Button>
            <Button variant="outline" onClick={handleTest} disabled={testing} data-testid="notif-send-test">
              {testing ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Send className="w-4 h-4 mr-1.5" />}
              Send test
            </Button>
            <Button
              variant="ghost"
              onClick={() => setConfirmReset(true)}
              disabled={!template.is_overridden}
              data-testid="notif-reset"
            >
              <RotateCcw className="w-4 h-4 mr-1.5" /> Reset to default
            </Button>
          </div>
        </div>

        <div className="space-y-2">
          <Label className="text-xs text-muted-foreground uppercase tracking-wider">Live preview</Label>
          <div className="rounded-lg border bg-background overflow-hidden" data-testid="notif-preview">
            <div className="px-4 py-2 border-b bg-muted/40 text-xs">
              <span className="text-muted-foreground">Subject: </span>
              <span className="font-medium">{renderPreview(subject) || '—'}</span>
            </div>
            <iframe
              title="email-preview"
              sandbox=""
              srcDoc={renderPreview(bodyHtml)}
              className="w-full bg-white"
              style={{ height: 460, border: 0 }}
            />
          </div>
          <p className="text-[11px] text-muted-foreground">
            Preview uses sample values; real sends use the live data per recipient.
          </p>
        </div>
      </div>

      <AlertDialog open={confirmReset} onOpenChange={setConfirmReset}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reset this template?</AlertDialogTitle>
            <AlertDialogDescription>
              Subject, body, and enabled state will all revert to the platform
              default for <span className="font-medium">{template.name}</span>.
              This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={resetting}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleReset} disabled={resetting} data-testid="notif-reset-confirm">
              {resetting ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : null}
              Reset
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
